import { Box, Button, Collapse, Stack } from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTruckModeLabel, materialStateEnum, truckModeEnum } from '../../../../constants';
import { getMaterialStateColor } from '../../../../constants/colors';
import { isTruckContextEnabled } from '../../../../constants/helpers/truck';
import { useCreateDailyTruckRideMutation, useUpdateDailyTruckRideMutation } from '../../../../hooks/mutations/useDailyTruckRideMutation';
import { useDumpsQuery } from '../../../../hooks/queries/useDumpsQuery';
import { useLoadNaturesQuery } from '../../../../hooks/queries/useLoadNaturesQuery';
import { useQuarriesQuery } from '../../../../hooks/queries/useQuarriesQuery';
import { useSitesQuery } from '../../../../hooks/queries/useSitesQuery';
import { useTrucksCompaniesQuery } from '../../../../hooks/queries/useTrucksCompaniesQuery';
import { useTrucksQuery } from '../../../../hooks/queries/useTrucksQuery';
import { useTrucksTypesQuery } from '../../../../hooks/queries/useTrucksTypesQuery';
import { useBoolean } from '../../../../hooks/useBoolean';
import { DailyTransferRide } from '../../../../services/apiSLTP/dailyReports/dailyTransfersRides';
import { DailyTruckRide } from '../../../../services/apiSLTP/dailyReports/dailyTrucksRides';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import CustomTimePicker from '../../../ui/CustomTimePicker';
import DropDown, { OptionType } from '../../../ui/DropDown';
import Icon from '../../../ui/Icon';
import IconButton from '../../../ui/IconButton';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody, { ModalProps } from '../../ModalBody';
import ManageDailyTransferRideModal from './ManageDailyTransferRideModal';
import { useDeleteDailyTransferRideMutation } from '../../../../hooks/mutations/useDailyTransferRideMutation';

interface ManageValidationDailyTruckRideModalProps extends ModalProps {
    activities: OptionType[];
    siteName?: string;
    refetch: () => void;
    dailyTruckRide: DailyTruckRide | null;
    currentDate: Date;
    initialActivityId?: string;
    initTruckId?: string | null;
}

const ManageValidationDailyTruckRideModal: React.FC<ManageValidationDailyTruckRideModalProps> = ({
    open,
    handleClose,
    activities,
    siteName,
    refetch,
    dailyTruckRide,
    currentDate,
    initialActivityId,
    initTruckId,
}) => {
    const { t } = useTranslation();
    const [selectedActivity, setSelectedActivity] = useState({
        label: activities.find((activity) => activity.value === dailyTruckRide?.activityId || activity.value === initialActivityId)?.label || '',
        value: dailyTruckRide?.activityId || initialActivityId || '',
    });
    const [currentTruckRideId, setCurrentTruckRideId] = useState(dailyTruckRide?._id);
    const transferModal = useBoolean(false);
    const [selectedDepartureTime, setSelectedDepartureTime] = useState<string | null>(null);
    const [selectedArrivalTime, setSelectedArrivalTime] = useState<string | null>(null);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);
    const [selectedSecondTime, setSelectedSecondTime] = useState<string | null>(null);

    const [selectedPlateNumber, setSelectedPlateNumber] = useState('');
    const [selectedCompany, setSelectedCompany] = useState({ label: '', value: '' });
    const [selectedType, setSelectedType] = useState({ label: '', value: '' });
    const [selectedTruck, setSelectedTruck] = useState<OptionType>({ label: '', value: '' });
    const [selectedMode, setSelectedMode] = useState({ label: '', value: '' });

    const [selectedDepartureLoadNature, setSelectedDepartureLoadNature] = useState({ label: '', value: '' });
    const [selectedArrivalLoadNature, setSelectedArrivalLoadNature] = useState({ label: '', value: '' });
    const [selectedDepartureDump, setSelectedDepartureDump] = useState({ label: '', value: '' });
    const [selectedArrivalDump, setSelectedArrivalDump] = useState({ label: '', value: '' });
    const [selectedOrigin, setSelectedOrigin] = useState({ label: '', value: '', group: '' });
    const [selectedDestination, setSelectedDestination] = useState({ label: '', value: '', group: '' });
    const [selectedDepartureTonnage, setSelectedDepartureTonnage] = useState('');
    const [selectedArrivalTonnage, setSelectedArrivalTonnage] = useState('');
    const [observations, setObservations] = useState('');
    const [isDecommissioned, setIsDecommissioned] = useState(false);
    const [selectedTransfer, setSelectedTransfer] = useState<DailyTransferRide | null>(null);

    const { mutate: deleteDailyTransferRide } = useDeleteDailyTransferRideMutation();

    const {
        data: dataCompanies,
        isLoading: isLoadingCompanies,
        isError: isErrorCompanies,
        isSuccess: isSuccessCompanies,
        refetch: refetchCompanies,
    } = useTrucksCompaniesQuery({ queries: { active: true } });

    const {
        data: dataTrucksTypes,
        isLoading: isLoadingTrucksTypes,
        isError: isErrorTrucksTypes,
        isSuccess: isSuccessTrucksTypes,
        refetch: refetchTrucksTypes,
    } = useTrucksTypesQuery({ queries: { active: true } });

    const {
        data: dataTrucks,
        isLoading: isLoadingTrucks,
        isError: isErrorTrucks,
        isSuccess: isSuccessTrucks,
        refetch: refetchTrucks,
    } = useTrucksQuery({ queries: { truckCompanyId: selectedCompany.value, active: true } });

    const {
        data: dataSites,
        isLoading: isLoadingSites,
        isError: isErrorSites,
        isSuccess: isSuccessSites,
        refetch: refetchSites,
    } = useSitesQuery({ queries: { active: true } });

    const {
        data: dataLoadNatures,
        isLoading: isLoadingLoadNatures,
        isError: isErrorLoadNatures,
        isSuccess: isSuccessLoadNatures,
        refetch: refetchLoadNatures,
    } = useLoadNaturesQuery({ queries: { active: true } });

    const {
        data: dataQuarries,
        isLoading: isLoadingQuarries,
        isError: isErrorQuarries,
        isSuccess: isSuccessQuarries,
        refetch: refetchQuarries,
    } = useQuarriesQuery({ queries: { active: true } });

    const {
        data: dataDumps,
        isLoading: isLoadingDumps,
        isError: isErrorDumps,
        isSuccess: isSuccessDumps,
        refetch: refetchDumps,
    } = useDumpsQuery({ queries: { active: true } });

    const {
        mutate: createTruckRide,
        isSuccess: isCreateTruckRideSuccess,
        isError: isCreateTruckRideError,
        isLoading: isCreateTruckRideLoading,
        error: createTruckRideError,
    } = useCreateDailyTruckRideMutation({
        onSuccess: (data, variables) => {
            setCurrentTruckRideId(data.data._id);
            refetch();
        },
    });

    const {
        mutate: updateTruckRide,
        isSuccess: isUpdateTruckRideSuccess,
        isError: isUpdateTruckRideError,
        isLoading: isUpdateTruckRideLoading,
        error: updateTruckRideError,
    } = useUpdateDailyTruckRideMutation({
        onSuccess: (data, variables) => {
            refetch();
        },
    });

    const handleChangePlateNumber = (newText: string) => {
        let input = newText.toUpperCase().replace(/[^A-Z0-9]/g, '');
        input = input.slice(0, 7);

        let formatted = '';

        if (input.length > 0) {
            formatted += input.slice(0, Math.min(2, input.length));
        }
        if (input.length > 2) {
            formatted += '-' + input.slice(2, Math.min(5, input.length));
        }
        if (input.length > 5) {
            formatted += '-' + input.slice(5, 7);
        }

        setSelectedPlateNumber(formatted);
    };

    const postDailyTruckRide = async () => {
        const isOwnCompany = dataCompanies?.find((value) => value._id === selectedCompany.value)?.isOwnCompany;
        const truck = dataTrucks?.find((value) => value._id === selectedTruck.value);
        if (isOwnCompany && !truck) return;

        createTruckRide({
            activityId: selectedActivity.value,
            truckId: selectedTruck.value,
            departureLoadNatureId: parseInt(selectedMode.value) === truckModeEnum.TRANSFER ? undefined : selectedDepartureLoadNature.value,
            arrivalLoadNatureId: parseInt(selectedMode.value) === truckModeEnum.TRANSFER ? undefined : selectedArrivalLoadNature.value,
            departureQuarryId:
                parseInt(selectedMode.value) === truckModeEnum.TRANSFER
                    ? undefined
                    : selectedOrigin.group === capitalizeFirstLetter(t('quarries'))
                      ? selectedOrigin.value
                      : undefined,
            arrivalQuarryId:
                parseInt(selectedMode.value) === truckModeEnum.TRANSFER
                    ? undefined
                    : selectedDestination.group === capitalizeFirstLetter(t('quarries'))
                      ? selectedDestination.value
                      : undefined,
            mode: parseInt(selectedMode.value),
            departureSiteId:
                parseInt(selectedMode.value) === truckModeEnum.TRANSFER
                    ? undefined
                    : selectedOrigin.group === capitalizeFirstLetter(t('sites'))
                      ? selectedOrigin.value
                      : undefined,
            arrivalSiteId:
                parseInt(selectedMode.value) === truckModeEnum.TRANSFER
                    ? undefined
                    : selectedDestination.group === capitalizeFirstLetter(t('sites'))
                      ? selectedDestination.value
                      : undefined,
            departureDumpId: parseInt(selectedMode.value) === truckModeEnum.TRANSFER ? undefined : selectedDepartureDump.value,
            arrivalDumpId: parseInt(selectedMode.value) === truckModeEnum.TRANSFER ? '' : selectedArrivalDump.value,
            observations: observations,
            isIncomplete: false,
            departureTime: selectedDepartureTime ? selectedDepartureTime : undefined,
            arrivalTime: selectedArrivalTime ? selectedArrivalTime : undefined,
            arrivalTonnage: selectedArrivalTonnage ? parseFloat(selectedArrivalTonnage) : undefined,
            departureTonnage: selectedDepartureTonnage ? parseFloat(selectedDepartureTonnage) : undefined,
            time: selectedTime ? selectedTime : undefined,
            secondTime: selectedSecondTime ? selectedSecondTime : undefined,
            isDecommissioned,
        });
    };

    const patchDailyTruckRide = async () => {
        if (!currentTruckRideId) return;
        updateTruckRide({
            id: currentTruckRideId,
            data: {
                activityId: selectedActivity.value,
                truckId: selectedTruck.value,
                departureLoadNatureId: parseInt(selectedMode.value) === truckModeEnum.TRANSFER ? null : selectedDepartureLoadNature.value,
                arrivalLoadNatureId: parseInt(selectedMode.value) === truckModeEnum.TRANSFER ? null : selectedArrivalLoadNature.value,
                departureQuarryId:
                    parseInt(selectedMode.value) === truckModeEnum.TRANSFER
                        ? null
                        : selectedOrigin.group === capitalizeFirstLetter(t('quarries'))
                          ? selectedOrigin.value
                          : null,
                arrivalQuarryId:
                    parseInt(selectedMode.value) === truckModeEnum.TRANSFER
                        ? null
                        : selectedDestination.group === capitalizeFirstLetter(t('quarries'))
                          ? selectedDestination.value
                          : null,
                mode: parseInt(selectedMode.value),
                departureSiteId:
                    parseInt(selectedMode.value) === truckModeEnum.TRANSFER
                        ? null
                        : selectedOrigin.group === capitalizeFirstLetter(t('sites'))
                          ? selectedOrigin.value
                          : null,
                arrivalSiteId:
                    parseInt(selectedMode.value) === truckModeEnum.TRANSFER
                        ? null
                        : selectedDestination.group === capitalizeFirstLetter(t('sites'))
                          ? selectedDestination.value
                          : null,
                departureDumpId: parseInt(selectedMode.value) === truckModeEnum.TRANSFER ? null : selectedDepartureDump.value,
                arrivalDumpId: parseInt(selectedMode.value) === truckModeEnum.TRANSFER ? null : selectedArrivalDump.value,
                observations: observations,
                departureTime: selectedDepartureTime ? selectedDepartureTime : null,
                arrivalTime: selectedArrivalTime ? selectedArrivalTime : null,
                arrivalTonnage: selectedArrivalTonnage ? parseFloat(selectedArrivalTonnage) : null,
                departureTonnage: selectedDepartureTonnage ? parseFloat(selectedDepartureTonnage) : null,
                time: selectedTime ? selectedTime : null,
                secondTime: selectedSecondTime ? selectedSecondTime : null,
                isDecommissioned,
            },
        });
    };

    const removeDailyTransfer = async (transferId: string) => {
        deleteDailyTransferRide(transferId, { onSettled: refetch });
    };

    useEffect(() => {
        if (dailyTruckRide) {
            setObservations(dailyTruckRide.observations || '');

            setSelectedDepartureLoadNature({
                label: dailyTruckRide.departureLoadNatureId?.designation || '',
                value: dailyTruckRide.departureLoadNatureId?._id || '',
            });
            setSelectedArrivalLoadNature({
                label: dailyTruckRide.arrivalLoadNatureId?.designation || '',
                value: dailyTruckRide.arrivalLoadNatureId?._id || '',
            });
            setSelectedMode({
                label:
                    capitalizeFirstLetter(
                        t(getTruckModeLabel(dailyTruckRide.mode !== undefined && dailyTruckRide.mode !== null ? dailyTruckRide.mode : -1))
                    ) || '',
                value: dailyTruckRide.mode?.toString() || '',
            });
            setSelectedOrigin({
                label: dailyTruckRide.departureQuarryId?.designation || dailyTruckRide.departureSiteId?.name || '',
                value: dailyTruckRide.departureQuarryId?._id || dailyTruckRide.departureSiteId?._id || '',
                group: dailyTruckRide.departureQuarryId ? capitalizeFirstLetter(t('quarries')) : capitalizeFirstLetter(t('sites')),
            });
            setSelectedDestination({
                label: dailyTruckRide.arrivalQuarryId?.designation || dailyTruckRide.arrivalSiteId?.name || '',
                value: dailyTruckRide.arrivalQuarryId?._id || dailyTruckRide.arrivalSiteId?._id || '',
                group: dailyTruckRide.arrivalQuarryId ? capitalizeFirstLetter(t('quarries')) : capitalizeFirstLetter(t('sites')),
            });
            setSelectedDepartureDump({
                value: dailyTruckRide.departureDumpId?._id || '',
                label: dailyTruckRide.departureDumpId?.designation || '',
            });
            setSelectedArrivalDump({
                value: dailyTruckRide.arrivalDumpId?._id || '',
                label: dailyTruckRide.arrivalDumpId?.designation || '',
            });
            setSelectedTruck({ label: dailyTruckRide.truckId?.designation, value: dailyTruckRide.truckId._id });
            setSelectedType({ label: dailyTruckRide.truckId.truckTypeId?.designation, value: dailyTruckRide.truckId.truckTypeId._id });
            setSelectedTime(dailyTruckRide.time || null);
            setSelectedSecondTime(dailyTruckRide.secondTime || null);
            setSelectedDepartureTime(dailyTruckRide.departureTime || null);
            setSelectedArrivalTime(dailyTruckRide.arrivalTime || null);
            setSelectedDepartureTonnage(dailyTruckRide.departureTonnage?.toString() || '');
            setSelectedArrivalTonnage(dailyTruckRide.arrivalTonnage?.toString() || '');
            setSelectedPlateNumber(dailyTruckRide.truckId?.plateNumber || '');
            setIsDecommissioned(dailyTruckRide.isDecommissioned || false);
            setCurrentTruckRideId(dailyTruckRide._id);
        }
    }, []);

    useEffect(() => {
        if (dataCompanies && !initTruckId) {
            const defaultCompany = dataCompanies.find((value) => value.isOwnCompany);
            if (defaultCompany)
                setSelectedCompany({
                    label: dailyTruckRide?.truckId.truckCompanyId.designation || defaultCompany?.designation || '',
                    value: dailyTruckRide?.truckId.truckCompanyId._id || defaultCompany?._id || '',
                });
        }
    }, [dataCompanies]);

    useEffect(() => {
        if (dataTrucks && initTruckId && !dailyTruckRide) {
            const defaultTruck = dataTrucks.find((truck) => truck._id === initTruckId);
            if (defaultTruck) {
                setSelectedCompany({
                    label: defaultTruck?.truckCompanyId?.designation || '',
                    value: defaultTruck?.truckCompanyId?._id || '',
                });

                if (defaultTruck.truckCompanyId.isOwnCompany) {
                    setSelectedTruck({
                        label: defaultTruck?.designation || '',
                        value: defaultTruck?._id || '',
                        group: defaultTruck?.truckTypeId?.designation || '',
                    });
                } else {
                    setSelectedTruck({
                        label: `[${defaultTruck?.plateNumber}] ${defaultTruck?.truckCompanyId.designation} ${defaultTruck?.truckTypeId?.designation} `,
                        value: defaultTruck?._id || '',
                        group: defaultTruck?.truckTypeId?.designation || '',
                    });
                }
            }
        }
    }, [dataTrucks]);

    return (
        <ModalBody
            title={t('trucksRides')}
            subtitle={!!currentTruckRideId ? t('update') : t('add')}
            description={siteName}
            disableSubmit={
                !selectedMode.value.length ||
                !selectedCompany.value.length ||
                (dataCompanies?.find((value) => value._id === selectedCompany.value)?.isOwnCompany
                    ? !selectedTruck.value.length
                    : selectedPlateNumber.length !== 9 || !selectedType.value.length)
            }
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                !!currentTruckRideId ? patchDailyTruckRide() : postDailyTruckRide();
            }}
            closeOnSubmit={!!currentTruckRideId}
        >
            <Stack spacing={2}>
                <Stack spacing={0.5}>
                    <DropDown
                        selectedItem={selectedActivity}
                        items={activities}
                        handleChange={(event, value) => setSelectedActivity({ value: value?.value || '', label: value?.label || '' })}
                        inputLabel={t('activityOf')}
                        required
                    />
                    <DropDown
                        selectedItem={selectedCompany}
                        items={dataCompanies?.map((company) => ({ label: company.designation, value: company._id })) || []}
                        handleChange={(event, value) => {
                            if (value?.value !== selectedCompany.value) setSelectedTruck({ value: '', label: '' });
                            setSelectedCompany({ value: value?.value || '', label: value?.label || '' });
                        }}
                        inputLabel={t('company')}
                        loading={isLoadingCompanies}
                        required
                        disabled={!!dailyTruckRide?._id}
                    />

                    <DropDown
                        selectedItem={selectedTruck}
                        items={
                            dataTrucks
                                ?.sort((a, b) => (a.truckTypeId.displayOrder ?? Infinity) - (b.truckTypeId.displayOrder ?? Infinity))
                                .map((truck) => ({
                                    label: !!truck.truckCompanyId.isOwnCompany
                                        ? truck.designation
                                        : `[${truck.plateNumber}] ${truck.truckCompanyId.designation} ${truck.truckTypeId?.designation}`,
                                    value: truck._id,
                                    group: truck.truckTypeId.designation,
                                })) || []
                        }
                        handleChange={(event, value) =>
                            setSelectedTruck({ value: value?.value || '', label: value?.label || '', group: value?.group || '' })
                        }
                        inputLabel={t('truck')}
                        loading={isLoadingTrucks}
                        required
                        grouped
                        sortGroups={false}
                        sortItems={false}
                        disabled={!!dailyTruckRide?._id}
                    />

                    {/*<Collapse in={!dataCompanies?.find((value) => value._id === selectedCompany.value)?.isOwnCompany}>
                        <div className="fx-c gap-5px">
                            <DropDown
                                selectedItem={selectedType}
                                items={dataTrucksTypes?.map((type) => ({ label: type.designation, value: type._id })) || []}
                                handleChange={(event, value) => {
                                    if (value?.value !== selectedType.value) setSelectedTruck({ value: '', label: '' });
                                    setSelectedType({ value: value?.value || '', label: value?.label || '' });
                                }}
                                inputLabel={t('type')}
                                loading={isLoadingTrucksTypes}
                                required
                                disabled={!!dailyTruckRide?._id}
                            />
                            <CustomTextInput
                                value={selectedPlateNumber}
                                onChange={handleChangePlateNumber}
                                label={t('plateNumber')}
                                maxLength={10}
                                placeholder="SL-069-TP"
                                required
                                disabled={!!dailyTruckRide?._id}
                            />
                        </div>
                    </Collapse>*/}
                </Stack>

                <DropDown
                    selectedItem={selectedMode}
                    items={Object.values(truckModeEnum).map((mode) => ({
                        label: capitalizeFirstLetter(t(getTruckModeLabel(mode))),
                        value: mode.toString(),
                    }))}
                    handleChange={(event, value) => setSelectedMode({ value: value?.value || '', label: value?.label || '' })}
                    inputLabel={t('mode')}
                    sortItems={false}
                    required
                />
                {!!currentTruckRideId && (
                    <>
                        {isTruckContextEnabled(parseInt(selectedMode.value), 'departure') && (
                            <Collapse in={isTruckContextEnabled(parseInt(selectedMode.value), 'departure')}>
                                <Stack spacing={0.5}>
                                    <CustomTimePicker
                                        value={selectedDepartureTime}
                                        label={capitalizeFirstLetter(t('departureTime'))}
                                        onChange={(value) => {
                                            setSelectedDepartureTime(value);
                                        }}
                                        referenceDate={dayjs(currentDate.toISOString())}
                                    />
                                    <Collapse in={dataTrucksTypes?.find((truck) => truck._id === selectedType.value)?.isDumpAllowed}>
                                        <DropDown
                                            selectedItem={selectedDepartureDump}
                                            items={dataDumps?.map((dump) => ({ label: dump.designation, value: dump._id })) || []}
                                            handleChange={(event, value) =>
                                                setSelectedDepartureDump({ value: value?.value || '', label: value?.label || '' })
                                            }
                                            inputLabel={t('dump')}
                                        />
                                    </Collapse>
                                    <DropDown
                                        selectedItem={selectedDepartureLoadNature}
                                        items={dataLoadNatures?.map((nature) => ({ label: nature.designation, value: nature._id })) || []}
                                        handleChange={(event, value) =>
                                            setSelectedDepartureLoadNature({ value: value?.value || '', label: value?.label || '' })
                                        }
                                        inputLabel={t('loadNature')}
                                    />
                                    <DropDown
                                        selectedItem={selectedOrigin}
                                        items={
                                            dataSites
                                                ?.map((site) => ({ label: site.name, value: site._id, group: capitalizeFirstLetter(t('sites')) }))
                                                .concat(
                                                    dataQuarries?.map((quarry) => ({
                                                        label: quarry.designation,
                                                        value: quarry._id,
                                                        group: capitalizeFirstLetter(t('quarries')),
                                                    })) || []
                                                ) || []
                                        }
                                        handleChange={(event, value) =>
                                            setSelectedOrigin({
                                                value: value?.value || '',
                                                label: value?.label || '',
                                                group: value?.group || '',
                                            })
                                        }
                                        inputLabel={t('origin')}
                                        grouped
                                    />
                                    <CustomTextInput
                                        value={selectedDepartureTonnage}
                                        onChange={setSelectedDepartureTonnage}
                                        label={t('tonnage')}
                                        startAdornment={<div>T</div>}
                                        type="number"
                                    />
                                </Stack>
                            </Collapse>
                        )}
                        {!!selectedMode.value.length && (
                            <Collapse in={!!selectedMode.value.length}>
                                <CustomTimePicker
                                    value={selectedTime}
                                    label={capitalizeFirstLetter(t('onSiteTime'))}
                                    onChange={(value) => {
                                        setSelectedTime(value);
                                    }}
                                    referenceDate={dayjs(currentDate.toISOString())}
                                />
                            </Collapse>
                        )}

                        {isTruckContextEnabled(parseInt(selectedMode.value), 'arrival') && (
                            <Collapse in={isTruckContextEnabled(parseInt(selectedMode.value), 'arrival')}>
                                <Stack spacing={0.5}>
                                    <CustomTimePicker
                                        value={selectedArrivalTime}
                                        label={capitalizeFirstLetter(t('arrivalTime'))}
                                        onChange={(value) => {
                                            setSelectedArrivalTime(value);
                                        }}
                                        referenceDate={dayjs(currentDate.toISOString())}
                                    />
                                    <Collapse in={dataTrucksTypes?.find((truck) => truck._id === selectedType.value)?.isDumpAllowed}>
                                        <DropDown
                                            selectedItem={selectedArrivalDump}
                                            items={dataDumps?.map((dump) => ({ label: dump.designation, value: dump._id })) || []}
                                            handleChange={(event, value) =>
                                                setSelectedArrivalDump({ value: value?.value || '', label: value?.label || '' })
                                            }
                                            inputLabel={t('dump')}
                                        />
                                    </Collapse>

                                    <DropDown
                                        selectedItem={selectedArrivalLoadNature}
                                        items={dataLoadNatures?.map((nature) => ({ label: nature.designation, value: nature._id })) || []}
                                        handleChange={(event, value) =>
                                            setSelectedArrivalLoadNature({ value: value?.value || '', label: value?.label || '' })
                                        }
                                        inputLabel={t('loadNature')}
                                    />
                                    <DropDown
                                        selectedItem={selectedDestination}
                                        items={
                                            dataSites
                                                ?.map((site) => ({ label: site.name, value: site._id, group: capitalizeFirstLetter(t('sites')) }))
                                                .concat(
                                                    dataQuarries?.map((quarry) => ({
                                                        label: quarry.designation,
                                                        value: quarry._id,
                                                        group: capitalizeFirstLetter(t('quarries')),
                                                    })) || []
                                                ) || []
                                        }
                                        handleChange={(event, value) =>
                                            setSelectedDestination({
                                                value: value?.value || '',
                                                label: value?.label || '',
                                                group: value?.group || '',
                                            })
                                        }
                                        inputLabel={t('destination')}
                                        grouped
                                    />
                                    <CustomTextInput
                                        value={selectedArrivalTonnage}
                                        onChange={setSelectedArrivalTonnage}
                                        label={t('tonnage')}
                                        startAdornment={<div>T</div>}
                                        type="number"
                                    />
                                </Stack>
                            </Collapse>
                        )}
                        {isTruckContextEnabled(parseInt(selectedMode.value), 'secondTimeOnSite') && (
                            <Collapse in={isTruckContextEnabled(parseInt(selectedMode.value), 'secondTimeOnSite')}>
                                <CustomTimePicker
                                    value={selectedSecondTime}
                                    label={capitalizeFirstLetter(t('onSiteBackTime'))}
                                    onChange={(value) => {
                                        setSelectedSecondTime(value);
                                    }}
                                    referenceDate={dayjs(currentDate.toISOString())}
                                />
                            </Collapse>
                        )}

                        {isTruckContextEnabled(parseInt(selectedMode.value), 'transfer') && (
                            <Collapse in={isTruckContextEnabled(parseInt(selectedMode.value), 'transfer')}>
                                {/*<div className="fx-c gap-5px">
                                    <div className="w-100 pad-15px fx-r fx-items-c gap-10px">
                                        {capitalizeFirstLetter(t('transfers'))}
                                        <div className="h-1px bg-lightgray w-100" />
                                        <IconButton
                                            iconName="addCircle"
                                            onClick={() => {
                                                transferModal.setTrue();
                                                setSelectedTransfer(null);
                                            }}
                                            title={capitalizeFirstLetter(t('add'))}
                                        />
                                    </div>
                                    <div className="fx-r w-300px">
                                        <div className=" max-h-300px of-y-auto w-100">
                                            {dailyTruckRide?.transfers.map((transfer, index) => (
                                                <div
                                                    className="border-radius-10px min-h-25px fx-r pad-l-5px fx-items-fs fx-justify-sb"
                                                    style={{ backgroundColor: getMaterialStateColor(transfer.state), marginTop: index === 0 ? 0 : 5 }}
                                                >
                                                    <div
                                                        onDoubleClick={() => {
                                                            transferModal.setTrue();
                                                            setSelectedTransfer(transfer);
                                                        }}
                                                        className="fx-c w-100 text-black"
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {(transfer.machineId?.designation ||
                                                            transfer.toolId?.designation ||
                                                            transfer.rentalName ||
                                                            transfer.rentalCompanyId?.designation) && (
                                                            <div>
                                                                {transfer.machineId?.designation ||
                                                                    transfer.toolId?.designation ||
                                                                    `${transfer.rentalName} ${transfer.rentalCompanyId?.designation || ''}`}
                                                            </div>
                                                        )}

                                                        <div>
                                                            <b>{transfer.state === materialStateEnum.NEW ? t('from') + ' : ' : t('to') + ' : '}</b>
                                                            {transfer.siteId?.name || transfer.quarryId?.designation}
                                                        </div>
                                                    </div>
                                                    <IconButton iconName="close" onClick={() => removeDailyTransfer(transfer._id)} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>*/}
                                <Stack spacing={1} px={3}>
                                    {dailyTruckRide?.transfers.map((transfer) => (
                                        <Box
                                            key={transfer._id}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                borderRadius: 1,
                                                backgroundColor: getMaterialStateColor(transfer.state),
                                                color: 'black',
                                                px: 1,
                                                py: 0.5,
                                            }}
                                        >
                                            <Box
                                                sx={{ flexGrow: 1, cursor: 'pointer' }}
                                                onDoubleClick={() => {
                                                    transferModal.setTrue();
                                                    setSelectedTransfer(transfer);
                                                }}
                                            >
                                                {(transfer.machineId?.designation ||
                                                    transfer.toolId?.designation ||
                                                    transfer.rentalName ||
                                                    transfer.rentalCompanyId?.designation) && (
                                                    <Box>
                                                        {transfer.machineId?.designation ||
                                                            transfer.toolId?.designation ||
                                                            `${transfer.rentalName} ${transfer.rentalCompanyId?.designation || ''}`}
                                                    </Box>
                                                )}

                                                <Box>
                                                    <b>{transfer.state === materialStateEnum.NEW ? t('from') + ' : ' : t('to') + ' : '}</b>
                                                    {transfer.siteId?.name || transfer.quarryId?.designation}
                                                </Box>
                                            </Box>

                                            <IconButton iconName="close" onClick={() => removeDailyTransfer(transfer._id)} sx={{ color: 'black' }} />
                                        </Box>
                                    ))}
                                    <Button
                                        variant="outlined"
                                        startIcon={<Icon name="add" />}
                                        onClick={() => {
                                            transferModal.setTrue();
                                            setSelectedTransfer(null);
                                        }}
                                    >
                                        {capitalizeFirstLetter(t('addTransfer'))}
                                    </Button>
                                </Stack>
                            </Collapse>
                        )}
                        <ToggleLine
                            checked={isDecommissioned}
                            onChange={(checked) => setIsDecommissioned(checked)}
                            title={capitalizeFirstLetter(t('decommissioned'))}
                        />
                        <CustomTextInput value={observations} onChange={(text) => setObservations(text)} label={t('observations')} multiline />
                    </>
                )}
                {transferModal.value && currentTruckRideId && (
                    <ManageDailyTransferRideModal
                        handleClose={transferModal.setFalse}
                        open={transferModal.value}
                        refetch={refetch}
                        truckRideId={currentTruckRideId}
                        transfer={selectedTransfer}
                        siteName={siteName || ''}
                    />
                )}
            </Stack>
        </ModalBody>
    );
};

export default ManageValidationDailyTruckRideModal;
